import { useEffect, useCallback, useRef } from 'react';
import { useBlocker } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUI } from '@/hooks/ui/useUI';


export interface UseUnsavedChangesWarningOptions {
  /** Callback to execute when user confirms leaving */
  onConfirmLeave: () => void;
  /** 
   * If provided, only blocks navigation when dirty.
   * If omitted (undefined), always blocks navigation (backward compatible).
   */
  isDirty?: boolean;
}

export interface UseUnsavedChangesWarningResult {
  /** Cancel handler that shows confirmation modal before leaving */
  handleCancelWithWarning: () => void;
  /**
   * Mark navigation as confirmed to bypass the blocker.
   * Use before programmatic navigation after successful save.
   */
  confirmNavigation: () => void;
}

/**
 * Hook to warn users when navigating away from a form.
 * 
 * Features:
 * - Shows confirmation modal when clicking cancel
 * - Blocks browser back/forward navigation with confirmation
 * - Shows browser native confirmation on tab close/refresh
 * 
 * @example
 * ```tsx
 * const { handleCancelWithWarning } = useUnsavedChangesWarning({
 *   onConfirmLeave: () => navigate('/list'),
 * });
 * ```
 */
export function useUnsavedChangesWarning({
  onConfirmLeave,
  isDirty,
}: UseUnsavedChangesWarningOptions): UseUnsavedChangesWarningResult {
  const { t: translate } = useTranslation();
  const { openModal, closeModal } = useUI();
  
  // Track if navigation was already confirmed (to prevent double modal)
  const navigationConfirmedRef = useRef(false);

  // Block browser back/forward navigation
  // - If isDirty is undefined (not provided), always block (backward compatible)
  // - If isDirty is provided, only block when form has changes
  const shouldBlock = isDirty ?? true;
  const blocker = useBlocker(() => !navigationConfirmedRef.current && shouldBlock);

  // Helper to show unsaved changes confirmation modal
  const showUnsavedChangesModal = useCallback((
    onConfirm: () => void,
    onCancel?: () => void,
  ) => {
    openModal('ConfirmModal', {
      variant: 'alert',
      icon: 'alert',
      title: translate('common:unsavedChanges.title'),
      bodyText: translate('common:unsavedChanges.message'),
      confirmText: translate('common:unsavedChanges.confirmLeave'),
      cancelText: translate('common:unsavedChanges.stayOnPage'),
      onConfirm: () => {
        closeModal('ConfirmModal');
        navigationConfirmedRef.current = true;
        onConfirm();
      },
      onCancel: () => {
        closeModal('ConfirmModal');
        onCancel?.();
      },
    });
  }, [openModal, closeModal, translate]);

  // Handle blocker state changes (back/forward button)
  useEffect(() => {
    if (blocker.state === 'blocked') {
      showUnsavedChangesModal(
        () => blocker.proceed(),
        () => blocker.reset(),
      );
    }
  }, [blocker, showUnsavedChangesModal]);

  // Handle browser beforeunload (tab close, refresh)
  // Only show warning if form is dirty (or isDirty not provided)
  // Note: Modern browsers show a generic message, custom messages are ignored for security
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (navigationConfirmedRef.current) return;
      if (!shouldBlock) return;
      event.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [shouldBlock]);

  // Cancel handler with confirmation modal
  // If form is not dirty, navigate directly without showing modal
  const handleCancelWithWarning = useCallback(() => {
    if (!shouldBlock) {
      // No changes, navigate directly
      navigationConfirmedRef.current = true;
      onConfirmLeave();
      return;
    }

    showUnsavedChangesModal(onConfirmLeave);
  }, [showUnsavedChangesModal, onConfirmLeave, shouldBlock]);

  // Expose function to mark navigation as confirmed (for successful save scenarios)
  const confirmNavigation = useCallback(() => {
    navigationConfirmedRef.current = true;
  }, []);

  return {
    handleCancelWithWarning,
    confirmNavigation,
  };
}
